import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useChatActionEmitter } from "@/hooks/useChatActions";
import { useEmployeeEmailReveal } from "@/hooks/useEmployeeEmailReveal";
import { useMarqueeCopy } from "@/hooks/useMarqueeCopy";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { ReloadIcon } from "@radix-ui/react-icons";
import { EmployeeDTO } from "@shaarp/common";
import { EllipsisIcon, Mail } from "lucide-react";
import { useState } from "react";
import { FiSend } from "react-icons/fi";
import { RiErrorWarningLine, RiRobot2Line } from "react-icons/ri";
import { NavLink } from "react-router-dom";
import { Button, buttonVariants } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { CopyableContactButton } from "./copyable-contact-button";
import { SignalEmailDialog } from "./dialog-signal-email";

export default function DataTableCellCompanyEmployeeEmail({
  employee,
  companyId,
  onEmailRevealed = () => {},
  compact = false,
  forceContactActions = false,
  isRevealLocked = false,
}: {
  employee: EmployeeDTO;
  companyId?: string;
  onEmailRevealed?: (
    update: Partial<EmployeeDTO> & { id: string }
  ) => void;
  compact?: boolean;
  /**
   * When true (e.g. search results), always offer the reveal button instead of
   * trusting the provider-reported status. Once a reveal has been attempted,
   * the regular status rules apply again.
   */
  forceContactActions?: boolean;
  /** Shows the reveal button disabled: a row about to be replaced. */
  isRevealLocked?: boolean;
}) {
  const { revealEmail } = useEmployeeEmailReveal();
  const marquee = useMarqueeCopy();
  const { sendChatMessage } = useChatActionEmitter();
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasAttempted, setHasAttempted] = useState<boolean>(false);
  const [showSignalDialog, setShowSignalDialog] = useState(false);

  const fetchEmail = async () => {
    setIsLoading(true);
    try {
      const revealed = await revealEmail({
        employeeId: employee.id,
        companyId,
        minimumDurationMs: 3000, // 3 seconds minimum loading time
      });

      // Null means the request failed and the hook already explained why:
      // nothing to report to the row, and no attempt to record either.
      if (revealed) {
        setHasAttempted(true);
        onEmailRevealed({
          id: employee.id,
          email: revealed.email ?? null,
          emailStatus: revealed.emailStatus,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const generatePromptAction = () => {
    sendChatMessage(
      t("components.dataTableCompanyEmployees.email.promptMessage", {
        firstName: employee.firstName,
        lastName: employee.lastName,
        email: employee.email,
        jobTitle: employee.jobTitle,
      })
    );
  };

  // The server resolves the email status per team: it stays "UNKNOWN" until
  // this team attempts a reveal, then reflects the persisted outcome
  // (VERIFIED / UNAVAILABLE). Once a reveal exists in the DB, that outcome must
  // survive a page refresh, so only force the reveal affordance while the team
  // has not attempted yet — `hasAttempted` alone is local state and is lost on
  // reload. Mirrors the phone cell.
  const hasTeamAttemptedReveal =
    employee.emailStatus != null && employee.emailStatus !== "UNKNOWN";

  const canForceReveal =
    forceContactActions &&
    !hasAttempted &&
    !hasTeamAttemptedReveal &&
    !employee.isBlacklisted;

  const isEmailAvailable =
    !employee.isBlacklisted &&
    (canForceReveal ||
      employee.emailStatus === "VERIFIED" ||
      employee.emailStatus === "UNKNOWN");

  if (!isEmailAvailable) {
    if (!employee.isBlacklisted && employee.emailStatus === "UNAVAILABLE") {
      return t("components.dataTableCompanyEmployees.email.notFound");
    }
    return t("components.dataTableCompanyEmployees.email.notAvailable");
  }

  if (employee.email) {
    const actionButtons = compact ? (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="rounded-l-none"
          >
            <EllipsisIcon className="size-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem asChild>
            <NavLink to={`mailto:${employee.email}`}>
              <FiSend className="size-3" />
              {t("components.dataTableCompanyEmployees.email.sendMail")}
            </NavLink>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={generatePromptAction}>
            <RiRobot2Line className="size-3" />
            {t("components.dataTableCompanyEmployees.email.generateHook")}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setShowSignalDialog(true)}
            data-testid="signal-email-button"
          >
            <RiErrorWarningLine className="size-3" />
            {t("components.dataTableCompanyEmployees.email.signalEmailButton")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ) : (
      <>
        <Tooltip>
          <TooltipTrigger asChild>
            <NavLink
              className={cn(
                buttonVariants({ variant: "outline", size: "sm" }),
                "rounded-none"
              )}
              to={`mailto:${employee.email}`}
            >
              <FiSend className="size-3" />
            </NavLink>
          </TooltipTrigger>
          <TooltipContent>
            {t("components.dataTableCompanyEmployees.email.sendMail")}
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="rounded-none"
              onClick={generatePromptAction}
            >
              <RiRobot2Line className="size-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {t("components.dataTableCompanyEmployees.email.generateHook")}
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button
                variant="outline"
                size="sm"
                className="rounded-l-none"
                type="button"
                onClick={() => setShowSignalDialog(true)}
                data-testid="signal-email-button"
              >
                <RiErrorWarningLine className="w-4 h-4" />
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {t("components.dataTableCompanyEmployees.email.signalEmailButton")}
          </TooltipContent>
        </Tooltip>
      </>
    );

    return (
      <div className="flex -space-x-px">
        <CopyableContactButton
          value={employee.email}
          copiedLabel={t("components.dataTableCompanyEmployees.email.copied")}
          marquee={marquee}
        />
        {actionButtons}
        <SignalEmailDialog
          open={showSignalDialog}
          onOpenChange={setShowSignalDialog}
          employee={employee}
          companyId={companyId}
          onReported={(motive: string) =>
            handleSignalEmailReported({ employee, motive, onEmailRevealed })
          }
        />
      </div>
    );
  }

  return (
    <>
      <Button
        variant="default"
        size="sm"
        onClick={fetchEmail}
        disabled={isLoading || isRevealLocked}
        className="gap-2"
      >
        {isLoading ? (
          <>
            <ReloadIcon className="w-4 h-4 animate-spin" />
            {t("components.dataTableCompanyEmployees.email.retrieving")}
          </>
        ) : (
          <>
            <Mail className="size-4" />
            {t("components.dataTableCompanyEmployees.email.retrieveEmail")}
          </>
        )}
      </Button>
    </>
  );
}

const handleSignalEmailReported = ({
  employee,
  motive,
  onEmailRevealed,
}: {
  employee: EmployeeDTO;
  motive: string;
  onEmailRevealed: (update: Partial<EmployeeDTO> & { id: string }) => void;
}) => {
  if (motive === "SIGNAL_BAN") {
    onEmailRevealed({
      id: employee.id,
      isBlacklisted: true,
    });
  } else if (motive === "SIGNAL_WRONG") {
    onEmailRevealed({
      id: employee.id,
      emailStatus: "SIGNAL_WRONG",
    });
  }
};
